import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { Project, UserProfile } from '@/lib/types'
import { cn, profileName } from '@/lib/utils'
import { Avatar } from './Avatar'
import { useNotify } from './ConfirmDialog'
import { X, Check, Loader2, UserPlus } from 'lucide-react'

interface Props {
  /** The channel to invite people to. Null = closed. */
  project: Project | null
  currentUserId: string
  /** Owner + members of this channel (candidate invitees). */
  memberIds: string[]
  profilesById: Record<string, UserProfile>
  /** Number of (active) cards in the channel — shown on the « existing » option. */
  cardCount: number
  onClose: () => void
  onInvite: (
    projectId: string,
    userIds: string[],
    opts: { existing: boolean; future: boolean },
  ) => void | Promise<void>
}

/** Subscribe one or more channel members to ALL of a channel's cards —
 *  existing only, or existing + future. Opened from the channel's kebab
 *  menu in the sidebar. */
export function InviteToCardsModal({
  project,
  currentUserId,
  memberIds,
  profilesById,
  cardCount,
  onClose,
  onInvite,
}: Props) {
  const { t } = useTranslation()
  const notify = useNotify()
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [scope, setScope] = useState<'existing' | 'future'>('future')
  const [busy, setBusy] = useState(false)

  // Reset whenever a (different) channel opens.
  useEffect(() => {
    if (!project) return
    setSelected(new Set())
    setScope('future')
    setBusy(false)
  }, [project])

  if (!project) return null

  function toggle(uid: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(uid)) next.delete(uid)
      else next.add(uid)
      return next
    })
  }

  async function invite() {
    if (!project || busy || selected.size === 0) return
    setBusy(true)
    try {
      await onInvite(project.id, Array.from(selected), {
        existing: true,
        future: scope === 'future',
      })
      // No success confirmation — adding people in copy is a low-stakes
      // action; just close. Errors still surface via notify() below.
      onClose()
    } catch (e) {
      notify({
        title: t('inviteCards.error'),
        message: e instanceof Error ? e.message : String(e),
        tone: 'error',
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/30 px-4 py-6 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex max-h-[88vh] w-full max-w-md flex-col overflow-hidden rounded-3xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] shadow-[var(--shadow-modal)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-[var(--color-border-soft)] px-6 py-4">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-dim)]">
              {t('inviteCards.eyebrow')}
            </div>
            <div className="mt-0.5 truncate text-[15px] font-semibold text-[var(--color-text-strong)]">
              {project.name}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-1.5 text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]"
            aria-label={t('common.close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto p-6">
          <p className="text-[12px] leading-relaxed text-[var(--color-text-dim)]">
            {t('inviteCards.description')}
          </p>

          {/* Scope */}
          <div>
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--color-text-dim)]">
              {t('inviteCards.scopeLabel')}
            </div>
            <div className="space-y-1.5">
              <ScopeRow
                selected={scope === 'future'}
                onClick={() => setScope('future')}
                label={t('inviteCards.scopeFuture')}
                hint={t('inviteCards.scopeFutureHint')}
              />
              <ScopeRow
                selected={scope === 'existing'}
                onClick={() => setScope('existing')}
                label={t('inviteCards.scopeExisting', { count: cardCount })}
                hint={t('inviteCards.scopeExistingHint')}
              />
            </div>
          </div>

          {/* People */}
          <div>
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--color-text-dim)]">
              {t('inviteCards.peopleLabel')}
            </div>
            {memberIds.length === 0 ? (
              <div className="rounded-xl border border-dashed border-[var(--color-border)] px-3 py-4 text-center text-[11.5px] text-[var(--color-text-dim)]">
                {t('inviteCards.noMembers')}
              </div>
            ) : (
              <div className="space-y-1">
                {memberIds.map((uid) => {
                  const isSel = selected.has(uid)
                  const isMe = uid === currentUserId
                  const name =
                    profileName(profilesById[uid]) ||
                    (isMe ? t('common.you') : t('inviteCards.unnamed'))
                  return (
                    <button
                      key={uid}
                      type="button"
                      onClick={() => toggle(uid)}
                      className={cn(
                        'flex w-full items-center gap-2.5 rounded-xl border px-3 py-2 text-left transition-colors',
                        isSel
                          ? 'border-[var(--color-accent)] bg-[var(--color-accent-bg)]'
                          : 'border-[var(--color-border-soft)] hover:bg-[var(--color-surface-2)]',
                      )}
                    >
                      <Avatar
                        profile={profilesById[uid]}
                        userId={uid}
                        className="h-7 w-7"
                        textClassName="text-[10px]"
                      />
                      <span className="min-w-0 flex-1 truncate text-[12.5px] text-[var(--color-text)]">
                        {name}
                        {isMe && (
                          <span className="ml-1 text-[10.5px] text-[var(--color-text-dim)]">
                            ({t('common.you').toLowerCase()})
                          </span>
                        )}
                      </span>
                      <span
                        className={cn(
                          'flex h-4 w-4 shrink-0 items-center justify-center rounded-md border',
                          isSel
                            ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-white'
                            : 'border-[var(--color-border)]',
                        )}
                      >
                        {isSel && <Check className="h-3 w-3" strokeWidth={3} />}
                      </span>
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-[var(--color-border-soft)] bg-[var(--color-surface-2)] px-6 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl px-3 py-1.5 text-[12px] font-medium text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-3)] hover:text-[var(--color-text)]"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={invite}
            disabled={busy || selected.size === 0}
            className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--color-accent)] px-3.5 py-1.5 text-[12px] font-semibold text-white transition-colors hover:bg-[var(--color-accent-hover)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <UserPlus className="h-3.5 w-3.5" />
            )}
            {t('inviteCards.invite')}
          </button>
        </div>
      </div>
    </div>
  )
}

function ScopeRow({
  selected,
  onClick,
  label,
  hint,
}: {
  selected: boolean
  onClick: () => void
  label: string
  hint: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-start gap-2.5 rounded-xl border px-3 py-2.5 text-left transition-colors',
        selected
          ? 'border-[var(--color-accent)] bg-[var(--color-accent-bg)]'
          : 'border-[var(--color-border-soft)] hover:border-[var(--color-border)]',
      )}
    >
      <span
        className={cn(
          'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
          selected
            ? 'border-[var(--color-accent)] bg-[var(--color-accent)]'
            : 'border-[var(--color-border)]',
        )}
      >
        {selected && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
      </span>
      <span className="min-w-0">
        <span className="block text-[12.5px] font-medium text-[var(--color-text)]">
          {label}
        </span>
        <span className="block text-[11px] text-[var(--color-text-dim)]">{hint}</span>
      </span>
    </button>
  )
}
